/* eslint-disable @typescript-eslint/no-explicit-any */
import './SummaryModal.scss';

import React, { useMemo } from 'react';
import { Modal } from '@airbus/components-react';
import { Close } from '@airbus/icons/react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooksTypes';
import { loadCheckerFleetFinAndAtaDetails } from '../../models/checkerDetailsModel/checkerDetailsThunk';
import { setCheckerState } from '../../models/appConfigModel/appConfigSlice';
import {
  resetClfState, setCheckerDetailOperatorChanged, setCheckerDetailsInitialLoad, setFleetDetailFilter, updateCheckerDetailsColumnFilterPayload, updateCheckerDetailsColumnFilterState, updateCheckerFilterSelection, updateCheckerFiltersOptions,
} from '../../models/checkerDetailsModel/checkerDetailsSlice';
import {
  columnFilterStateUpdater, loadCheckerAcFamilyOptions, loadCheckerDetailsTable, loadCheckerFinAndAtaOptions,
} from '../../utils/commonUtil';
import { columns } from '../CheckerDetailsTable/CheckerDetailsTableStructure';

type SummaryData = {
  [key: string]: string | undefined;
  descriptive?: string;
  recommendation?: string;
  risk_assessment?: string;
};
interface SummaryModalProps {
  open: boolean;
  onClose: () => void;
  // eslint-disable-next-line react/require-default-props
  fleetDetails?: any;
  checkerFleetState: any;
  checkerDetailState: any;
}

const SummaryModal: React.FC<SummaryModalProps> = ({
  open, onClose, fleetDetails, checkerFleetState, checkerDetailState,
}) => {
  const dispatch = useAppDispatch();
  const overAllSummary = useAppSelector(
    (state) => state.checkerFleet,
  );
  const featureToggleConfig = useAppSelector((state) => state.featureToggleDetails?.data);
  const featureToggle = featureToggleConfig?.airnavx_no_fin === 'true';
  const { checkerDetailFiltersSelected, fleet_detail_filter_selected: fleetDetailFilterSelected } = checkerDetailState;

  const getSecureRandom = () => {
    const cryptoRef = globalThis?.crypto;
    if (!cryptoRef) {
      return 0.5;
    }
    const array = new Uint32Array(1);
    cryptoRef.getRandomValues(array);
    return array[0] / 4294967296;
  };

  const parseSummaryData = (data?: any): SummaryData => {
    if (!data) return {};
    if (typeof data === 'object') {
      if (data?.overall_summary) {
        return parseSummaryData(data.overall_summary);
      }

      return Object.entries(data).reduce((acc, [key, value]) => {
        if (typeof value === 'string') {
          acc[key] = value;
        }
        return acc;
      }, {} as SummaryData);
    }

    const trimmed = data.trim();
    const withoutOuterQuotes = (trimmed.startsWith('"') && trimmed.endsWith('"'))
      ? trimmed.slice(1, -1)
      : trimmed;

    const tryParse = (value: string) => {
      try {
        return JSON.parse(value) as SummaryData;
      } catch {
        return null;
      }
    };

    const direct = tryParse(withoutOuterQuotes);
    if (direct) return direct;

    const repairedAtaQuotes = withoutOuterQuotes.replaceAll(
      /ATA_Chapter\(([^)]*)\)/g,
      (_match: string, args: string) => `ATA_Chapter(${args.replaceAll('"', String.raw`\"`)})`,
    );

    const repaired = tryParse(repairedAtaQuotes);
    return repaired ?? {};
  };
  const cardClickDispatch = (ataValues: string[]) => {
    dispatch(resetClfState());
    dispatch(setCheckerState({ selectedCheckerTab: 1 }));
    dispatch(setCheckerDetailsInitialLoad({ ...checkerDetailState, checkerDetailsInitialLoad: false }));

    // If opened from top gear (no fleetDetails), load using current filter state only.
    if (!fleetDetails?.operator_code_icao || !fleetDetails?.ac_type || !fleetDetails?.id_aircraft_registration) {
      loadCheckerDetailsTable({
        operator: fleetDetailFilterSelected?.operator_name || checkerDetailFiltersSelected?.operator_name || '',
        aircraft: fleetDetailFilterSelected?.aircraft_type || [],
        aircraft_subtype: fleetDetailFilterSelected?.aircraft_subtype || [],
        msn: fleetDetailFilterSelected?.selected_msn || [],
        fin: fleetDetailFilterSelected?.selected_fin || [],
        ata: ataValues,
        fin_status: [],
        status_part_check: [],
        acr_source: [],
        mis_source: [],
        acr_source_status: [],
        mis_source_status: [],
        featureToggle,
        dispatch,
      });
      return;
    }

    const selectedOperator = checkerFleetState?.fleet_view_filter_selected?.selected_operator?.[0];

    dispatch(
      updateCheckerFiltersOptions({
        operator_code_icao: checkerFleetState?.fleet_view_filters?.operators_list,
        ac_type: checkerFleetState?.fleet_view_filters?.ac_type_list,
        ac_sub_type: checkerFleetState?.data?.[selectedOperator]?.reduce((result: any[], item: any) => {
          if (item?.ac_type === fleetDetails?.ac_type && item.ac_type && !result.includes(item.ac_subtype)) {
            result.push(item.ac_subtype);
          }
          return result;
        }, []),
        id_aircraft_registration: checkerFleetState?.data?.[selectedOperator]?.reduce((result: any[], item: any) => {
          if (
            item?.ac_type === fleetDetails?.ac_type
            && item?.ac_subtype === fleetDetails?.ac_subtype
            && item.ac_type
          ) {
            result.push(item.id_aircraft_registration);
          }
          return result;
        }, []),
      }),
    );

    dispatch(updateCheckerFilterSelection({
      operator_name: fleetDetails.operator_code_icao,
      selected_msn: [fleetDetails.id_aircraft_registration],
      aircraft_type: [fleetDetails.ac_type],
      aircraft_family: fleetDetails.ac_family,
      aircraft_subtype: [fleetDetails.ac_subtype],
      selected_ata: [],
      selected_fin: [],
    }));

    const filterValue = {
      operator_name: fleetDetails.operator_code_icao,
      selected_msn: [fleetDetails.id_aircraft_registration],
      aircraft_type: [fleetDetails.ac_type],
      aircraft_family: fleetDetails.ac_family,
      aircraft_subtype: [fleetDetails.ac_subtype],
      selected_ata: [],
      selected_fin: [],
    };

    dispatch(setCheckerDetailOperatorChanged({ ...checkerDetailState, ifOperatorChanged: false, ifAircraftTypeChanged: false }));
    dispatch(setFleetDetailFilter({ ...checkerDetailState, fleet_detail_filter_selected: filterValue }));

    const filterDispatchParam: string[] = [];
    columnFilterStateUpdater(
      dispatch,
      filterDispatchParam,
      filterDispatchParam,
      updateCheckerDetailsColumnFilterState,
      updateCheckerDetailsColumnFilterPayload,
      filterDispatchParam,
    );

    const selectedOperatorData = checkerFleetState?.data?.[selectedOperator] || [];
    loadCheckerAcFamilyOptions(fleetDetails.operator_code_icao, dispatch, selectedOperatorData);
    loadCheckerFinAndAtaOptions(
      fleetDetails.operator_code_icao,
      [fleetDetails.ac_type],
      [fleetDetails.id_aircraft_registration],
      dispatch,
      loadCheckerFleetFinAndAtaDetails,
    );

    loadCheckerDetailsTable({
      operator: fleetDetails.operator_code_icao,
      aircraft: [fleetDetails.ac_type],
      aircraft_subtype: [fleetDetails.ac_subtype],
      msn: [fleetDetails.id_aircraft_registration],
      fin: [],
      ata: ataValues,
      fin_status: [],
      status_part_check: [],
      acr_source: [],
      mis_source: [],
      acr_source_status: [],
      mis_source_status: [],
      featureToggle,
      dispatch,
    }, columns);
  };
  const renderAtaChapterLinks = (text?: string): React.ReactNode => {
    if (!text) return '';
    const pattern = /ATA_Chapter\(([^)]+)\)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match = pattern.exec(text);

    while (match) {
      const [fullMatch, args] = match;
      const start = match.index;
      if (start > lastIndex) {
        parts.push(text.slice(lastIndex, start));
      }
      const anchorText = `ATA_Chapter(${args})`;
      const ataValues = args
        .split(',')
        .map((item) => item.replaceAll(/['"]/g, '').trim())
        .filter((item) => item.length > 0);
      parts.push(
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          // variant="underlined"
          title="Ata chapters"
          onClick={(event) => {
            event.preventDefault();
            cardClickDispatch(ataValues);
          }}
          target="_blank"
          rel="noreferrer"
          to="#"
        >
          {anchorText}
        </Link>,
      );
      lastIndex = start + fullMatch.length;
      match = pattern.exec(text);
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  const summaryData = parseSummaryData(
    fleetDetails?.smart_insights ?? fleetDetails?.map_value ?? overAllSummary?.overall_summary,
  );
  const formatSummaryKey = (key: string) => key
    .split('_')
    .filter((part) => part.trim().length > 0)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
  const bubbles = useMemo(
    () => Array.from({ length: 10 }).map((_, index) => {
      const left = getSecureRandom() * 90; // vw
      const size = 30 + getSecureRandom() * 70; // px
      const delay = getSecureRandom() * 7; // s
      const duration = 12 + getSecureRandom() * 10; // s
      const opacity = 0.4 + getSecureRandom() * 0.4;
      const key = `bubble-${index}-${left}-${size}-${delay}-${duration}-${opacity}`;
      return {
        key,
        style: {
          left: `${left}vw`,
          width: `${size}px`,
          height: `${size}px`,
          animationDelay: `${delay}s`,
          animationDuration: `${duration}s`,
          opacity,
        },
      };
    }),
    [],
  );

  return (
    <Modal open={open} onClose={onClose} className="summary-modal">
      {/* Floating bubbles overlay (randomized) */}
      <div className="summary-modal-bubbles">
        {bubbles.map((bubble) => (
          <div
            key={bubble.key}
            className="summary-modal-bubble"
            style={bubble.style}
          />
        ))}
      </div>
      <div
        className="summary-modal-content"
      >
        <button
          type="button"
          className="summary-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          <Close />
        </button>
        <div className="summary-modal-text bubble">
          <section className="note-section">
            {Object.entries(summaryData ?? {})
              .filter(([, value]) => typeof value === 'string' && value.trim().length > 0)
              .map(([key, value]) => (
                <React.Fragment key={key}>
                  <strong>{formatSummaryKey(key)}</strong>
                  <p>
                    {renderAtaChapterLinks(value)}
                  </p>
                </React.Fragment>
              ))}
          </section>
        </div>
      </div>
    </Modal>
  );
};

export default SummaryModal;
